import { ClassesFilter, PatriarcasFilter, PlanosFilter } from '../../default/fod-default.pipe';
import { Component, OnInit, Input, EventEmitter, Output, OnChanges, signal, NO_ERRORS_SCHEMA } from '@angular/core';
import { Plano, Classe, Patriarca } from '../../../../../models/edocs.model';
import { Enums } from '../../../../../shared/enums';
import { EDocsService } from '../../../../../services/edocs.service';
import { Utils } from '../../../../../shared/utils';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { ToastrService } from 'ngx-toastr';
import { ConfigSchemaSectionDocumentClass } from '../../../../../models/config-schema.model';
import { IMPORTS } from './_imports';

@Component({
    selector: 'document-class',
    templateUrl: './document-class.component.html',
    styleUrl: './document-class.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class DocumentClassComponent implements OnInit, OnChanges {
    // #region [Type props]
    Utils = Utils;
    // #endregion

    // #region [props]
    patriarcaId = '';
    patriarcas: Patriarca[] = [];
    planoId = '';
    planos: Plano[] = [];
    classeId = '';
    classes: Classe[] = [];
    // #endregion

    // #region [signals]
    $model = signal(null as ConfigSchemaSectionDocumentClass);
    $flowDefinitionOrganizationId = signal(null as string);
    $isReadOnlyMode = signal(false);
    $patriarcaDisplay = signal('');
    $patriarcaNome = signal(null as string);
    $selectablePatriarcas = signal([] as Patriarca[]);
    $planoDisplay = signal('');
    $planoNome = signal(null as string);
    $selectablePlanos = signal([] as Plano[]);
    $classeDisplay = signal('');
    $selectableClasses = signal([] as Classe[]);
    // #endregion

    // #region [Input/Output]
    @Input() _model: ConfigSchemaSectionDocumentClass;
    @Input() _flowDefinitionOrganizationId: string;
    @Input() _isReadOnlyMode: boolean;
    @Output() _modelChange = new EventEmitter<ConfigSchemaSectionDocumentClass>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private edocsService: EDocsService,
        private planosFilter: PlanosFilter,
        private classesFilter: ClassesFilter,
        private patriarcasFilter: PatriarcasFilter
    ) { }

    ngOnInit() {
        let interval = setInterval(async () => {
            if (this.$model() != null) {
                clearInterval(interval);

                const response_Patriarcas = await this.edocsService.getPatriarcas();

                if (!response_Patriarcas.isSuccess) {
                    this.toastr.error(response_Patriarcas.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                this.patriarcas = response_Patriarcas.data;
                this.patriarcas.sort((a, b) => {
                    let first = a.sigla || a.nome || '';
                    let second = b.sigla || b.nome || '';
                    return first.localeCompare(second);
                });
                Utils.setSignal(this.$selectablePatriarcas, this.patriarcas);
            }
        }, 100);
    }

    ngOnChanges() {
        this.$model.set(this._model);
        this.$flowDefinitionOrganizationId.set(this._flowDefinitionOrganizationId);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    patriarcaDisplayChange() {
        this.$selectablePatriarcas.set(this.patriarcasFilter.transform(this.patriarcas, this.$patriarcaDisplay()));
    }

    async patriarcaIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.patriarcaId = event.option.value.id;
        }

        if (this.patriarcaId != '') {
            const response = await this.edocsService.getPlanosAtivos(this.patriarcaId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.planos = response.data;
            this.planos.sort((a, b) => {
                let first = a.codigo || a.nome || '';
                let second = b.codigo || b.nome || '';
                return first.localeCompare(second);
            });
            Utils.setSignal(this.$selectablePlanos, this.planos);
            this.$planoDisplay.set('');

            let selectedPatriarch = this.patriarcas.find(x => x.id == this.patriarcaId);
            this.$planoNome.set(`${selectedPatriarch.sigla} - ${selectedPatriarch.nome}`.toUpperCase());
            this.$patriarcaNome.set(`${event.option.value.sigla} - ${event.option.value.nome}`.toUpperCase());
        } else {
            this.planoId = '';
            this.planos = [];
            this.$selectablePlanos.set([]);
            this.$patriarcaNome.set(null);
            Utils.updSignal(this.$model, x => {
                x.name = null;
                x.id = null;
                return x;
            });

            this._modelChange.emit(this.$model());
        }
    }

    displayPatriarcaId(option): string {
        return option != null && option != ''
            ? `${option.sigla} - ${option.nome.toUpperCase()}`
            : '';
    }

    clearPatriarca() {
        this.patriarcaId = '';
        this.$patriarcaDisplay.set('');
        Utils.setSignal(this.$selectablePatriarcas, this.patriarcas);
        this.planos = [];
        this.clearPlano();
    }

    planoDisplayChange() {
        this.$selectablePlanos.set(this.planosFilter.transform(this.planos, this.$planoDisplay()));
    }

    async planoIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.planoId = event.option.value.id;
        }

        if (this.planoId != '') {
            let response = await this.edocsService.getClassesAtivas(this.planoId);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.classes = response.data;
            this.classes.sort((a, b) => a.codigo.localeCompare(b.codigo));
            this.classes.sort((a, b) => {
                let first = a.codigo || a.nome || '';
                let second = b.codigo || b.nome || '';
                return first.localeCompare(second);
            });
            Utils.setSignal(this.$selectableClasses, this.classes);
            this.$classeDisplay.set('');

            let selectedPlan = this.planos.find(x => x.id == this.planoId);
            this.$planoNome.set(`${selectedPlan.codigo} - ${selectedPlan.nome.toUpperCase()}`);
        } else {
            this.classeId = '';
            this.classes = [];
            this.$selectableClasses.set([]);
            this.$planoNome.set(null);
            Utils.updSignal(this.$model, x => {
                x.name = null;
                x.id = null;
                return x;
            });

            this._modelChange.emit(this.$model());
        }
    }

    displayPlanoId(option): string {
        return option != null && option != ''
            ? `${option.codigo} - ${option.nome.toUpperCase()}`
            : '';
    }

    clearPlano() {
        this.planoId = '';
        this.$planoDisplay.set('');
        Utils.setSignal(this.$selectablePlanos, this.planos);
        this.classes = [];
        this.clearClasse();
    }

    classeDisplayChange() {
        this.$selectableClasses.set(this.classesFilter.transform(this.classes, this.$classeDisplay()));
    }

    classeIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.classeId = event.option.value.id;
        }

        if (this.classeId == '') {
            this.classeId = null;
            Utils.updSignal(this.$model, x => {
                x.name = null;
                return x;
            });
        } else {
            let selectedClass = this.classes.find(x => x.id == this.classeId);
            Utils.updSignal(this.$model, x => {
                x.name = `${selectedClass.codigo} - ${selectedClass.nome.toUpperCase()}`;
                return x;
            });
        }

        Utils.updSignal(this.$model, x => {
            x.id = this.classeId;
            return x;
        });

        this._modelChange.emit(this.$model());
    }

    documentClassNameChange(event) {
        Utils.updSignal(this.$model, x => {
            x.name = event;
            return x;
        });
    }

    displayClasseId(option): string {
        return option != null && option != ''
            ? `${option.codigo} - ${option.nome.toUpperCase()}`
            : '';
    }

    clearClasse() {
        this.classeId = '';
        this.$classeDisplay.set('');
        Utils.setSignal(this.$selectableClasses, this.classes);
    }

    clearClassificacao() {
        Utils.updSignal(this.$model, x => {
            x.name = null;
            x.id = null;
            return x;
        });

        this._modelChange.emit(this.$model());
    }

    isActualClasse(option: Classe): boolean {
        return option.unidadeArquivamento;
    }

    // #region [===== private methods =====]
    // #endregion
}
